// src/services/calendarConfigService.js
//
// Per-site Research Calendar configuration — rooms, timezone, label, and
// whether patient names appear in event titles.
//
// This replaced three hardcoded copies of the room list (ScheduleVisitModal,
// researchCalendarRooms, appsscript/research-calendar.gs). Rooms are now data:
// a site edits them in Manage → Calendar and every surface follows.
//
// This file is the I/O half. All shape/validation logic lives in the
// Firebase-free calendarConfigSchema.js so it can be unit tested.
//
// The connection details (Apps Script URL, shared secret, OAuth tokens) are NOT
// here and are not readable by any client — see setCalendarConnection() below,
// which posts them to a Cloud Function that writes them somewhere rules deny.

import { doc, getDoc, setDoc } from "firebase/firestore";
import { db, auth } from "../firebase";
import { FUNCTIONS_BASE } from "../config/functionsBase";
import { activeSiteUsesLegacyCalendar } from "./activeTenant";
import {
  getCalendarIntegrationDocPath,
  getCalendarIntegrationDocPathFor,
} from "./firestorePaths";
import {
  DEFAULT_CALENDAR_CONFIG,
  shapeCalendarConfig,
  toCalendarConfigPayload,
} from "./calendarConfigSchema";

const FN_BASE = FUNCTIONS_BASE;

// Re-exported so components can keep importing everything calendar-config from
// one place.
export {
  DEFAULT_CALENDAR_ROOMS,
  CALENDAR_ROOM_PALETTE,
  DEFAULT_CALENDAR_CONFIG,
  paletteEntry,
  validateCalendarConfig,
  OTHER_ROOM,
} from "./calendarConfigSchema";

/**
 * Read the ACTIVE site's calendar config. Never throws — returns the defaults
 * on any failure so the calendar UI always renders.
 */
export const fetchCalendarConfig = async () => {
  // A site with NO config doc only inherits the original Dubois calendar if it
  // IS that site. Any other unconfigured site is "none" — otherwise the UI would
  // claim a calendar is connected while the server (isLegacyCalendarSite in
  // functions/index.js) correctly refuses to touch one, and the page would show
  // an empty week rather than "not set up".
  const unconfigured = () => ({
    ...DEFAULT_CALENDAR_CONFIG,
    provider: activeSiteUsesLegacyCalendar() ? "apps-script" : "none",
    exists: false,
  });

  try {
    const snap = await getDoc(doc(db, getCalendarIntegrationDocPath()));
    if (!snap.exists()) return unconfigured();
    return shapeCalendarConfig(snap.data(), true);
  } catch (e) {
    // Missing rules or offline: fall back rather than breaking the page.
    return unconfigured();
  }
};

/**
 * Read a SPECIFIC site's config. Used by the admin console, which manages sites
 * other than the one the admin is currently working in — reading via the
 * active-site helper there would show (and then overwrite) the wrong site.
 *
 * Unlike fetchCalendarConfig this DOES throw: an admin editing settings needs to
 * see "couldn't load" rather than silently be handed defaults and save them over
 * a real config.
 */
export const fetchCalendarConfigForSite = async (tenantId, siteId) => {
  const snap = await getDoc(
    doc(db, getCalendarIntegrationDocPathFor(tenantId, siteId))
  );
  return shapeCalendarConfig(snap.data(), snap.exists());
};

/**
 * Write the ACTIVE site's calendar config. Display half only — connection
 * secrets can't be set from here.
 */
export const saveCalendarConfig = async (config) => {
  const payload = toCalendarConfigPayload(config);
  await setDoc(doc(db, getCalendarIntegrationDocPath()), payload, { merge: true });
  return payload;
};

/** Write a SPECIFIC site's config (admin console). */
export const saveCalendarConfigForSite = async (tenantId, siteId, config) => {
  const payload = toCalendarConfigPayload(config);
  await setDoc(
    doc(db, getCalendarIntegrationDocPathFor(tenantId, siteId)),
    payload,
    { merge: true }
  );
  return payload;
};

// ---------------------------------------------------------------------------
// Connection secrets — via Cloud Function only.
//
// The endpoint URL and shared secret live at
// integrations/calendar/private/connection, which Firestore rules deny to every
// client. They're written by the `calendarConnection` Cloud Function (Admin SDK,
// tenant-admin gated) and can never be read back — the status call returns
// booleans, not values. Rotating means setting a new one, not viewing the old.
// ---------------------------------------------------------------------------

const callConnectionFn = async (body) => {
  const user = auth.currentUser;
  if (!user) throw new Error("You must be signed in.");
  const idToken = await user.getIdToken();
  const resp = await fetch(`${FN_BASE}/calendarConnection`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(body),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(data.error || `Request failed (${resp.status}).`);
  return data;
};

/**
 * Whether this site has connection details stored.
 * @returns {Promise<{hasUrl:boolean, hasSecret:boolean, urlHint:string}>}
 * `urlHint` is a truncated, non-functional fragment (enough to recognise which
 * deployment it is, not enough to call it).
 */
export const fetchCalendarConnectionStatus = (tenantId, siteId) =>
  callConnectionFn({ action: "status", tenantId, siteId });

/**
 * Store this site's connection details. Omit `sharedSecret` to leave the
 * existing one untouched; pass "" explicitly to clear it.
 */
export const setCalendarConnection = (tenantId, siteId, { scriptUrl, sharedSecret }) =>
  callConnectionFn({ action: "set", tenantId, siteId, scriptUrl, sharedSecret });

/**
 * End-to-end check that a site's calendar actually answers: runs a real `list`
 * for today through the same authenticated path bookings use.
 *
 * Read-only by design — it must never create a test event on a live clinical
 * calendar. A successful empty result is still a pass; what's being proven is
 * that auth, the config doc, the connection secret and the calendar all line up.
 */
export const testCalendarConnection = async (tenantId, siteId) => {
  const user = auth.currentUser;
  if (!user) throw new Error("You must be signed in.");
  const idToken = await user.getIdToken();

  const now = new Date();
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);

  const resp = await fetch(`${FN_BASE}/researchCalendar`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      tenantId,
      siteId,
      action: "list",
      timeMin: start.toISOString(),
      timeMax: end.toISOString(),
    }),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(data.error || `Test failed (${resp.status}).`);
  return { ok: true, count: (data.events || []).length };
};
